import * as Sentry from '@sentry/react';
import { ApiError } from '@core/api/apiClient';
import { markFullStoryEvent, getFullStoryReplayUrl } from '@core/analytics/fullstory';

/**
 * True when the ApiError represents a client-side transport failure that
 * cannot be acted on from the app — the browser had no connectivity or
 * the request exceeded its timeout. These are not bugs and reporting
 * them floods the Sentry issue list with noise that recycles forever.
 *
 * The two codes are assigned in `apiClient.ts`: NETWORK_ERROR when axios
 * reports no response object, TIMEOUT_ERROR when the request aborts past
 * the 30s client timeout.
 */
export function isExpectedClientTransportError(error: unknown): boolean {
  return error instanceof ApiError && error.isNetworkError();
}

/**
 * Normalize an endpoint path for stable Sentry grouping.
 *
 * Replaces numeric ids, UUIDs, and long alphanumeric slugs with placeholders so
 * that `/api/reservations/22542407` and `/api/reservations/22542408` share the
 * same fingerprint.
 */
export function normalizeEndpoint(raw?: string): string {
  if (!raw) return 'unknown';
  let path: string;
  try {
    path = new URL(raw, 'http://dummy').pathname;
  } catch {
    path = raw.split('?')[0];
  }
  return path
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, '/:uuid')
    .replace(/\/[A-Za-z0-9_-]{20,}(?=\/|$)/g, '/:token');
}

/**
 * Capture API errors with structured context and explicit fingerprint.
 *
 * Fingerprint is [tag, status, code, method, endpoint] so Sentry groups events
 * by the *kind* of failure (e.g. "401 on POST /api/traditional-grab") rather
 * than by stack trace (which would collapse all ApiErrors into a single issue).
 */
export function captureApiError(error: unknown, context?: Record<string, unknown>) {
  if (isExpectedClientTransportError(error)) return;

  if (!(error instanceof ApiError)) {
    const source = typeof context?.source === 'string' ? context.source : undefined;
    Sentry.withScope((scope) => {
      if (source) scope.setTag('source', source);
      if (context) scope.setContext('error_context', context);
      Sentry.captureException(error);
    });
    return;
  }

  const endpoint = normalizeEndpoint(error.endpoint ?? (context?.url as string | undefined));
  const method = error.method ?? (context?.method as string | undefined) ?? 'UNKNOWN';
  const status = error.status ?? 0;

  const source = typeof context?.source === 'string' ? context.source : undefined;

  Sentry.withScope((scope) => {
    scope.setTag('api.status', String(status || 'unknown'));
    scope.setTag('api.code', error.code);
    scope.setTag('api.endpoint', endpoint);
    scope.setTag('api.method', method);
    if (error.serverCode) scope.setTag('api.server_code', error.serverCode);
    if (source) scope.setTag('source', source);
    scope.setContext('api_request', {
      endpoint: error.endpoint,
      method,
      status,
      serverCode: error.serverCode,
      serverErrors: error.errors,
      ...context,
    });
    scope.setLevel(status >= 500 ? 'error' : 'warning');
    scope.setFingerprint([
      'api-error',
      String(status || 'unknown'),
      error.code,
      method,
      endpoint,
    ]);
    Sentry.captureException(error);
  });
}

/**
 * Capture payment processing errors with processor tag and structured context.
 *
 * Fingerprint uses processor + status + endpoint so Stripe/MP failures are
 * separate issues and distinct failure modes (token reuse, 500, 412) group apart.
 */
export function capturePaymentError(
  error: unknown,
  processor: 'stripe' | 'mercadopago',
  context?: Record<string, unknown>,
) {
  if (isExpectedClientTransportError(error)) return;

  // A missing-reservationId failure is already captured at the throw site with
  // a dedicated fingerprint (captureMissingReservationId). The commit pipeline
  // re-throws it through here, but re-capturing would create a noisy duplicate
  // issue grouped as a generic payment error. Name-check avoids importing the
  // feature-layer error class into a core util.
  if (error instanceof Error && error.name === 'MissingReservationIdError') return;

  const isApi = error instanceof ApiError;
  const endpoint = isApi
    ? normalizeEndpoint(error.endpoint)
    : normalizeEndpoint(context?.url as string | undefined);
  const status = isApi ? error.status ?? 0 : 0;
  const code = isApi ? error.code : 'NON_API_ERROR';
  const message = error instanceof Error ? error.message : String(error);

  const source = typeof context?.source === 'string' ? context.source : undefined;

  Sentry.withScope((scope) => {
    scope.setTag('payment.processor', processor);
    scope.setTag('api.status', String(status || 'unknown'));
    scope.setTag('api.code', code);
    scope.setTag('api.endpoint', endpoint);
    if (isApi && error.serverCode) {
      scope.setTag('api.server_code', error.serverCode);
    }
    if (source) scope.setTag('source', source);
    if (context) scope.setContext('payment', context);
    if (isApi) {
      scope.setContext('api_request', {
        endpoint: error.endpoint,
        method: error.method,
        status,
        serverCode: error.serverCode,
        serverErrors: error.errors,
      });
    }
    scope.setLevel(status >= 500 || !isApi ? 'error' : 'warning');
    scope.setFingerprint([
      'payment-error',
      processor,
      String(status || 'unknown'),
      code,
      // Normalize the backend message for grouping (strip unique tokens)
      message.replace(/tok_[A-Za-z0-9]+/g, 'tok_:id').slice(0, 120),
      endpoint,
    ]);
    Sentry.captureException(error);
  });
}

/**
 * Structured context attached to a MercadoPago SDK load outcome capture.
 * `reason` distinguishes a fetch that errored from one that stalled past
 * the per-attempt timeout without ever firing onerror.
 */
export interface MpSdkLoadContext {
  restaurantId: number;
  attempts: number;
  reason?: 'script-error' | 'timeout';
}

/**
 * Capture the outcome of a MercadoPago SDK load that needed retries.
 *
 * 'failed' means the guest is stuck on "No se pudo cargar el sistema de
 * pago" — a lost reservation unless they reload. 'recovered' means a retry
 * saved the session; captured as a warning so the volume and geography of
 * flaky loads stay measurable (event geo comes from the client IP).
 * Dedicated fingerprint so both group apart from payment/api errors.
 */
export function captureMpSdkLoadOutcome(
  outcome: 'failed' | 'recovered',
  context: MpSdkLoadContext,
) {
  Sentry.withScope((scope) => {
    scope.setTag('payment.processor', 'mercadopago');
    scope.setTag('mp_sdk.outcome', outcome);
    scope.setContext('mp_sdk_load', { ...context });
    scope.setLevel(outcome === 'failed' ? 'error' : 'warning');
    scope.setFingerprint(['mp-sdk-load', outcome]);
    Sentry.captureMessage(
      outcome === 'failed'
        ? 'MercadoPago SDK failed to load after retries'
        : 'MercadoPago SDK loaded after retry',
      outcome === 'failed' ? 'error' : 'warning',
    );
  });
}

/**
 * Structured context attached to a missing-reservationId capture.
 * `endpoint` is a stable label for the write that 200'd without an id.
 */
export interface MissingReservationIdContext {
  endpoint: string;
  isWaitingList: boolean;
  flowType?: string;
  guaranteeType?: string;
  restaurant?: number;
}

/**
 * Capture the "endpoint reported success but returned no reservationId" anomaly.
 *
 * This is a backend contract violation, not a payment/user error: the write
 * endpoint 2xx'd but produced no reservation to confirm. Advancing the user to
 * /booking-final in that state would show a "confirmed" screen for a reservation
 * that does not exist. Dedicated fingerprint so it groups apart from
 * payment/api errors and is easy to alert on.
 */
export function captureMissingReservationId(context: MissingReservationIdContext) {
  const endpoint = normalizeEndpoint(context.endpoint);

  // Tag this session in FullStory + grab the replay URL so the Sentry
  // event below carries a one-click deep-link to the moment this guard
  // fired. Both no-op cleanly if FullStory isn't loaded (dev / preview /
  // blocked / SDK not yet established).
  markFullStoryEvent('reservation_missing_id', {
    endpoint,
    is_waiting_list: context.isWaitingList,
    flow_type: context.flowType ?? '<null>',
    guarantee_type: context.guaranteeType ?? '<null>',
  });
  const fullstoryReplayUrl = getFullStoryReplayUrl();

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('reservation.missing_id', 'true');
    scope.setTag('api.endpoint', endpoint);
    scope.setContext('reservation_commit', { ...context });
    if (fullstoryReplayUrl) {
      scope.setExtra('fullstoryReplayUrl', fullstoryReplayUrl);
    }
    scope.setFingerprint(['missing-reservation-id', endpoint]);
    Sentry.captureMessage(
      'Reservation endpoint returned success without reservationId',
      'error',
    );
  });
}

export { captureAuthError } from './authErrors';
export type { AuthErrorName, AuthErrorContext } from './authErrors';
